/**
 * PLA-142: V3 Upload State Types
 * 
 * Defines the UX state model for V3 video upload and pipeline progress.
 * These types map backend states to user-facing labels without modifying backend.
 * 
 * Stage 1: UX State Model Confirmation
 */

import type { AnalysisStage } from './analysisProgress';

/**
 * UX states for V3 upload and pipeline progress.
 * Maps to backend states but uses user-friendly naming.
 */
export type V3UploadState =
  | 'ready_to_upload'      // No active pipeline - user can upload
  | 'uploading_video'      // status='uploaded' OR analysis_stage='uploading'
  | 'analyzing_pose'       // analysis_stage='processing'
  | 'analyzing_swing'      // analysis_stage='telemetry'
  | 'building_coaching'    // analysis_stage='coaching'
  | 'analysis_ready'       // status='ready'
  | 'analysis_failed';     // status='failed'

/**
 * Backend status values from swing_runs.status
 */
export type SwingRunStatus = 'uploaded' | 'processing' | 'ready' | 'failed';

/**
 * Minimal swing run data needed for state derivation
 */
export interface ActivePipelineRun {
  id: string;
  status: SwingRunStatus;
  analysis_stage: AnalysisStage | null;
  analysis_progress: number | null;
  created_at: string;
}

/**
 * UX configuration for each state
 */
export interface V3UploadStateConfig {
  state: V3UploadState;
  label: string;
  statusText: string;
  progressType: 'none' | 'determinate' | 'indeterminate' | 'complete' | 'error';
  canUpload: boolean;
}

/**
 * Configuration for all V3 upload states
 */
export const V3_UPLOAD_STATES: Record<V3UploadState, V3UploadStateConfig> = {
  ready_to_upload: {
    state: 'ready_to_upload',
    label: 'Ready to upload',
    statusText: 'Upload a video to analyze your swing',
    progressType: 'none',
    canUpload: true,
  },
  uploading_video: {
    state: 'uploading_video',
    label: 'Uploading your video',
    statusText: 'Uploading video...',
    progressType: 'determinate',
    canUpload: false,
  },
  analyzing_pose: {
    state: 'analyzing_pose',
    label: 'Analyzing your swing',
    statusText: 'Extracting swing data...',
    progressType: 'indeterminate',
    canUpload: false,
  },
  analyzing_swing: {
    state: 'analyzing_swing',
    label: 'Analyzing swing mechanics',
    statusText: 'Analyzing your technique...',
    progressType: 'indeterminate',
    canUpload: false,
  },
  building_coaching: {
    state: 'building_coaching',
    label: 'Creating your coaching',
    statusText: 'Crafting personalized feedback...',
    progressType: 'indeterminate',
    canUpload: false,
  },
  analysis_ready: {
    state: 'analysis_ready',
    label: 'Coaching ready',
    statusText: 'Your swing analysis is ready!',
    progressType: 'complete',
    canUpload: true,
  },
  analysis_failed: {
    state: 'analysis_failed',
    label: 'Something went wrong',
    statusText: 'Analysis failed. Please try again.',
    progressType: 'error',
    canUpload: true, // Allow retry
  },
};

/**
 * Disabled upload copy per PLA-142 spec
 */
export const UPLOAD_DISABLED_COPY = 
  "Your swing is being analyzed. You can upload another video once this process is complete.";

/**
 * Derive UX state from backend run data.
 * Returns 'ready_to_upload' if no active pipeline exists.
 * 
 * @param run - Active pipeline run or null
 * @returns V3UploadState for UI rendering
 */
export function deriveV3UploadState(run: ActivePipelineRun | null): V3UploadState {
  // No active pipeline - user can upload
  if (!run) {
    return 'ready_to_upload';
  }

  // Terminal states take precedence
  if (run.status === 'failed') {
    return 'analysis_failed';
  }
  
  if (run.status === 'ready') {
    return 'analysis_ready';
  }

  // Map analysis_stage to UX state
  switch (run.analysis_stage) {
    case 'uploading':
      return 'uploading_video';
    case 'processing':
      return 'analyzing_pose';
    case 'telemetry':
      return 'analyzing_swing';
    case 'coaching':
      return 'building_coaching';
    case 'complete':
      return 'analysis_ready';
    default:
      // Default to uploading_video for uploaded status without analysis_stage
      return 'uploading_video';
  }
}

/**
 * Get state configuration for a given state
 */
export function getV3UploadStateConfig(state: V3UploadState): V3UploadStateConfig {
  return V3_UPLOAD_STATES[state];
}

/**
 * Check if upload should be disabled based on state
 */
export function isUploadDisabled(state: V3UploadState): boolean {
  return !V3_UPLOAD_STATES[state].canUpload;
}

/**
 * Check if a run represents an active pipeline (blocking new uploads)
 */
export function isActivePipeline(run: ActivePipelineRun): boolean {
  // Active if status is 'uploaded' (awaiting orchestration) or 'processing'
  return run.status === 'uploaded' || run.status === 'processing';
}

/**
 * Backend status values that indicate an active pipeline
 * Used for database queries
 */
export const ACTIVE_PIPELINE_STATUSES: SwingRunStatus[] = ['uploaded', 'processing'];
